import { useQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import api from '../lib/api';
import { devLog, devWarn } from '../lib/dev-logger';
import { useAuthStore } from '../stores/auth.store';

const WORKSPACE_PATH_REGEX = /^\/w\/([^/]+)/i;

export function usePermissions() {
  const location = useLocation();
  const workspaceId = location.pathname.match(WORKSPACE_PATH_REGEX)?.[1] ?? null;
  const accessToken = useAuthStore((state) => state.accessToken);

  const { data: permissions, isLoading } = useQuery<string[]>({
    queryKey: ['permissions', 'me', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];

      devLog('permissions', 'Buscando permissões do usuário', {
        workspaceId,
        path: location.pathname,
      });

      try {
        const response = await api.get<{ data: string[] }>('/permissions/me', {
          headers: {
            'X-Workspace-Id': workspaceId,
          },
        });
        const perms = response.data.data;
        const permissionList = Array.isArray(perms) ? perms : [];

        devLog('permissions', 'Permissões carregadas', {
          workspaceId,
          count: permissionList.length,
        });

        // Ensure we always return an array
        return permissionList;
      } catch (error: any) {
        devWarn('permissions', 'Erro ao buscar permissões', {
          workspaceId,
          status: error?.response?.status,
          message: error?.response?.data?.message,
        });
        throw error;
      }
    },
    enabled: !!workspaceId && !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const hasPermission = (permissionKey: string): boolean => {
    if (!permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissions.includes(permissionKey);
  };

  const waitingForSession = !!workspaceId && !accessToken;

  return {
    permissions: Array.isArray(permissions) ? permissions : [],
    isLoading: waitingForSession || isLoading,
    hasPermission,
  };
}
